import { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * Premium loyalty trail — a winding bamboo pathway with milestone stamps.
 *  - progress:  number of completed purchases in current cycle
 *  - target:    number required
 *  - completed: boolean, whether current cycle is completed (reward pending)
 *  - variant:   "hero" (larger) | "compact"
 */
export default function LoyaltyTrail({ progress = 0, target = 6, completed = false, variant = "hero" }) {
  const stops = target + 1; // 0..target (final = reward)
  const H = variant === "compact" ? 130 : 200;
  const W = variant === "compact" ? 520 : 720;
  const PAD_X = 40;
  const AMP = variant === "compact" ? 22 : 36;

  const points = useMemo(() => {
    const xs = [];
    const step = (W - PAD_X * 2) / (stops - 1);
    for (let i = 0; i < stops; i++) {
      const x = PAD_X + i * step;
      const y = H / 2 + Math.sin((i / (stops - 1)) * Math.PI * 2.2) * AMP;
      xs.push({ x, y });
    }
    return xs;
  }, [stops, H, W, AMP]);

  const path = useMemo(() => {
    if (!points.length) return "";
    return points.reduce((acc, p, i) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const prev = points[i - 1];
      const cx1 = (prev.x + p.x) / 2;
      return `${acc} C ${cx1} ${prev.y}, ${cx1} ${p.y}, ${p.x} ${p.y}`;
    }, "");
  }, [points]);

  // Path length calc (for progress reveal)
  const doneRatio = Math.min(1, progress / target);
  const pandaP = points[Math.min(progress, target)] || points[0];

  return (
    <div className="relative w-full" data-testid="loyalty-trail" style={{ maxWidth: W }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet" className="overflow-visible">
        <defs>
          <linearGradient id="ltTrailBase" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#4a3a1a" />
            <stop offset="100%" stopColor="#3b2c14" />
          </linearGradient>
          <linearGradient id="ltTrailFill" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#FFC93C" />
            <stop offset="100%" stopColor="#FF9800" />
          </linearGradient>
          <radialGradient id="ltStamp" cx="30%" cy="30%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0.35" />
          </radialGradient>
        </defs>

        {/* Base bamboo path */}
        <path d={path} fill="none" stroke="url(#ltTrailBase)" strokeWidth={variant === "compact" ? 10 : 14} strokeLinecap="round" opacity="0.8" />
        {/* Ticked bamboo joints along the path */}
        {points.map((p, i) => (
          <circle key={`b${i}`} cx={p.x} cy={p.y} r={variant === "compact" ? 6 : 8} fill="#5a4520" opacity={0.9} />
        ))}

        {/* Progress fill using stroke-dasharray */}
        <motion.path
          d={path}
          fill="none"
          stroke="url(#ltTrailFill)"
          strokeWidth={variant === "compact" ? 10 : 14}
          strokeLinecap="round"
          pathLength={1}
          initial={{ strokeDasharray: "1 1", strokeDashoffset: 1 }}
          animate={{ strokeDashoffset: 1 - doneRatio }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />

        {/* Milestone stamps */}
        {points.map((p, i) => {
          const done = i < progress;
          const next = i === progress && !completed;
          const isReward = i === target;
          return (
            <motion.g
              key={`m${i}`}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.08 * i, type: "spring", stiffness: 220, damping: 16 }}
            >
              <circle cx={p.x} cy={p.y} r={variant === "compact" ? 16 : 22} fill={done ? "#FFC93C" : isReward ? "#FF4F5A" : "#2B2B2B"} stroke={next ? "#FFC93C" : "#1a1a1a"} strokeWidth={next ? 3 : 2} />
              <circle cx={p.x} cy={p.y} r={variant === "compact" ? 16 : 22} fill="url(#ltStamp)" opacity="0.3" />
              <text x={p.x} y={p.y + 4} textAnchor="middle" fontSize={variant === "compact" ? 14 : 18} fontWeight="800" fill={done ? "#2B2B2B" : "#FFF8F0"}>
                {isReward ? "★" : (done ? "✓" : i + 1)}
              </text>
              {next && (
                <circle cx={p.x} cy={p.y} r={variant === "compact" ? 22 : 30} fill="none" stroke="#FFC93C" strokeWidth="2" strokeOpacity="0.4">
                  <animate attributeName="r" values={variant === "compact" ? "16;28;16" : "22;38;22"} dur="2s" repeatCount="indefinite" />
                  <animate attributeName="stroke-opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite" />
                </circle>
              )}
            </motion.g>
          );
        })}

        {/* Panda walker */}
        <motion.g
          animate={{ x: pandaP.x, y: pandaP.y - (variant === "compact" ? 40 : 55) }}
          transition={{ type: "spring", stiffness: 90, damping: 16, delay: 0.2 }}
        >
          <g transform={`translate(-${variant === "compact" ? 18 : 24}, -${variant === "compact" ? 18 : 24})`}>
            <svg viewBox="0 0 60 60" width={variant === "compact" ? 36 : 48} height={variant === "compact" ? 36 : 48}>
              <circle cx="16" cy="18" r="8" fill="#111" />
              <circle cx="44" cy="18" r="8" fill="#111" />
              <ellipse cx="30" cy="34" rx="22" ry="20" fill="#fff" stroke="#2B2B2B" strokeWidth="2" />
              <ellipse cx="22" cy="30" rx="5" ry="6" fill="#111" transform="rotate(-10 22 30)" />
              <ellipse cx="38" cy="30" rx="5" ry="6" fill="#111" transform="rotate(10 38 30)" />
              <circle cx="22" cy="30" r="1.5" fill="#fff" />
              <circle cx="38" cy="30" r="1.5" fill="#fff" />
              <ellipse cx="30" cy="40" rx="3" ry="2" fill="#111" />
              <path d={completed ? "M 24 46 Q 30 52 36 46" : "M 25 46 Q 30 50 35 46"} stroke="#2B2B2B" strokeWidth="1.8" fill="none" strokeLinecap="round" />
            </svg>
          </g>
        </motion.g>
      </svg>
    </div>
  );
}
